import { INVALID_FAMILYPORTAL_EMAIL_MESSAGE, OTP_ACTION_RESEND, OTP_ACTION_SEND, OTP_PURPOSE_FORGOT_PASSWORD, OTP_ROLE_CAREGIVER, OTP_ROLE_FAMILY_MEMBER, sendEmailOtp, validateEmailOtp, validateFamilyportalEmail } from "./familyportalOtpApi";
import constantURL from "./ConstantURL";
import { postLoginData } from "./index";
import { isPasswordValid, PASSWORD_CRITERIA_INVALID_MESSAGE } from "../utils/passwordCriteria";
import { parseLockedUntilMs } from "../utils/forgotPasswordResendLock";

export const FORGOT_PASSWORD_OTP_LENGTH = 6;
export const FORGOT_PASSWORD_OTP_ACTION_SEND = OTP_ACTION_SEND;
export const FORGOT_PASSWORD_OTP_ACTION_RESEND = OTP_ACTION_RESEND;
export const RESET_PASSWORD_ERROR_MESSAGE = "Please try again later.";

export const isForgotPasswordAttemptsLockError = (message = "", data = null) => {
  const normalizedMessage = String(message || "").toLowerCase();

  if (normalizedMessage.includes("reached limit") || normalizedMessage.includes("forgot password attempt") || normalizedMessage.includes("forgot-password attempt") || normalizedMessage.includes("too many forgot password")) {
    return true;
  }

  if (!data) {
    return false;
  }

  return Number(data.forgetAttemptsUsed) >= 3 && (data.daysRemaining != null || data.lockDurationDays != null || data.lockedUntil != null);
};

export const buildForgotPasswordLockState = (data = {}, message = "") => {
  const attemptsUsed = Number(data.forgetAttemptsUsed) || 3;
  const daysRemaining = Number(data.daysRemaining ?? data.lockDurationDays) || 30;
  const lockedUntilMs = parseLockedUntilMs(data.lockedUntil);
  const retryOnDate = lockedUntilMs ? new Date(lockedUntilMs) : new Date(Date.now() + daysRemaining * 24 * 60 * 60 * 1000);

  return {
    attemptsUsed,
    daysRemaining,
    retryOnFormatted: retryOnDate.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    }),
    message: String(message || "").trim(),
  };
};

const validatedForgotPasswordRoles = new Map();

export const resolveForgotPasswordRole = (emailId) => {
  const normalizedEmail = String(emailId || "")
    .trim()
    .toLowerCase();
  const cachedRole = validatedForgotPasswordRoles.get(normalizedEmail);
  if (cachedRole) {
    return cachedRole;
  }

  const domain = normalizedEmail.split("@")[1];
  return domain === "caregiver.com" ? OTP_ROLE_CAREGIVER : OTP_ROLE_FAMILY_MEMBER;
};

const rememberValidatedRole = (emailId, role) => {
  const normalizedEmail = String(emailId || "")
    .trim()
    .toLowerCase();
  const normalizedRole = String(role || "")
    .trim()
    .toUpperCase();
  if (!normalizedEmail || !normalizedRole) {
    return normalizedRole;
  }
  validatedForgotPasswordRoles.set(normalizedEmail, normalizedRole);
  return normalizedRole;
};

export const clearForgotPasswordValidatedRole = (emailId) => {
  const normalizedEmail = String(emailId || "")
    .trim()
    .toLowerCase();
  if (normalizedEmail) {
    validatedForgotPasswordRoles.delete(normalizedEmail);
  }
};

export const clearAllForgotPasswordValidatedRoles = () => {
  validatedForgotPasswordRoles.clear();
};

/**
 * Looks up which roles (ADMIN/CAREGIVER/FAMILYMEMBER) have a password set for
 * this email, so the UI can auto-send the OTP for a single role or ask the
 * user to pick when more than one applies.
 */
export const validateForgetPasswordCredential = async (emailId) => {
  const normalizedEmail = String(emailId || "")
    .trim()
    .toLowerCase();

  if (!normalizedEmail) {
    return { success: false, message: "Please enter your email address.", roles: [] };
  }

  try {
    const response = await postLoginData(constantURL.authValidateForgetPasswordCredential, {
      emailId: normalizedEmail,
    });

    if (!response) {
      return { success: false, message: "Unexpected server response.", roles: [] };
    }

    if (response.status === "success") {
      const roles = Array.isArray(response.data?.roles) ? response.data.roles.map((role) => String(role || "").trim().toUpperCase()).filter(Boolean) : [];

      if (!roles.length) {
        return { success: false, message: response.message || "User does not exist.", roles: [] };
      }

      return { success: true, message: response.message || "Credential validated successfully.", roles };
    }

    return { success: false, message: response.message || "User does not exist.", roles: [] };
  } catch {
    return { success: false, message: RESET_PASSWORD_ERROR_MESSAGE, roles: [] };
  }
};

export const sendForgotPasswordOtp = async (emailId, action = FORGOT_PASSWORD_OTP_ACTION_SEND, presetRole = "") => {
  const normalizedEmail = String(emailId || "")
    .trim()
    .toLowerCase();
  const normalizedPresetRole = String(presetRole || "")
    .trim()
    .toUpperCase();

  // A role picked on the login screen means validateCredential already
  // confirmed this account exists there — skip the family-portal email check
  // entirely rather than let it second-guess a login the user already passed.
  let role = normalizedPresetRole ? rememberValidatedRole(normalizedEmail, normalizedPresetRole) : resolveForgotPasswordRole(normalizedEmail);

  if (action === OTP_ACTION_SEND && !normalizedPresetRole) {
    const validation = await validateFamilyportalEmail(normalizedEmail, {
      purpose: OTP_PURPOSE_FORGOT_PASSWORD,
    });
    if (!validation.success) {
      if (isForgotPasswordAttemptsLockError(validation.message, validation.data)) {
        return {
          success: false,
          isForgotPasswordLocked: true,
          message: validation.message,
          data: validation.data,
        };
      }

      return {
        success: false,
        message: validation.message || INVALID_FAMILYPORTAL_EMAIL_MESSAGE,
        data: validation.data,
      };
    }

    role = rememberValidatedRole(normalizedEmail, validation.data?.role || role);
  }

  const sendResult = await sendEmailOtp({
    email: normalizedEmail,
    purpose: OTP_PURPOSE_FORGOT_PASSWORD,
    action,
    role,
  });

  if (!sendResult.success) {
    return sendResult;
  }

  return {
    ...sendResult,
    data: {
      ...(sendResult.data || {}),
      role,
    },
  };
};

export const verifyForgotPasswordOtp = (emailId, otp) =>
  validateEmailOtp({
    email: emailId,
    otpCode: otp,
    purpose: OTP_PURPOSE_FORGOT_PASSWORD,
    role: resolveForgotPasswordRole(emailId),
  });

const sanitizeForgotLoginCode = (value) =>
  String(value || "")
    .replace(/\D/g, "")
    .slice(0, 4);

export const isForgotPasswordCaregiverRole = (emailId) => resolveForgotPasswordRole(emailId) === OTP_ROLE_CAREGIVER;

export const resetForgotLoginCode = async ({ emailId, loginCode, confirmLoginCode }) => {
  const code = sanitizeForgotLoginCode(loginCode);
  const confirm = sanitizeForgotLoginCode(confirmLoginCode);

  if (code.length !== 4) {
    return { success: false, message: "Login code must be 4 digits." };
  }
  if (code !== confirm) {
    return { success: false, message: "Login codes do not match." };
  }

  const normalizedEmail = String(emailId || "")
    .trim()
    .toLowerCase();
  if (!normalizedEmail) {
    return {
      success: false,
      message: RESET_PASSWORD_ERROR_MESSAGE,
      redirectToLogin: true,
    };
  }

  try {
    const response = await postLoginData(constantURL.authSetPassword, {
      emailId: normalizedEmail,
      password: code,
      role: OTP_ROLE_CAREGIVER,
    });

    if (response?.status === "success") {
      clearForgotPasswordValidatedRole(normalizedEmail);
      return {
        success: true,
        message: response.message || "Your login code has been reset. You can sign in now.",
      };
    }

    return {
      success: false,
      message: RESET_PASSWORD_ERROR_MESSAGE,
      redirectToLogin: true,
    };
  } catch {
    return {
      success: false,
      message: RESET_PASSWORD_ERROR_MESSAGE,
      redirectToLogin: true,
    };
  }
};

export const resetForgotPassword = async ({ emailId, password, confirmPassword }) => {
  if (!isPasswordValid(password)) {
    return { success: false, message: PASSWORD_CRITERIA_INVALID_MESSAGE };
  }

  if (password !== confirmPassword) {
    return { success: false, message: "Passwords do not match." };
  }

  const normalizedEmail = String(emailId || "")
    .trim()
    .toLowerCase();
  if (!normalizedEmail) {
    return {
      success: false,
      message: RESET_PASSWORD_ERROR_MESSAGE,
      redirectToLogin: true,
    };
  }

  try {
    const response = await postLoginData(constantURL.authSetPassword, {
      emailId: normalizedEmail,
      password,
      role: resolveForgotPasswordRole(normalizedEmail),
    });

    if (response?.status === "success") {
      clearForgotPasswordValidatedRole(normalizedEmail);
      return {
        success: true,
        message: response.message || "Password updated successfully.",
      };
    }

    return {
      success: false,
      message: RESET_PASSWORD_ERROR_MESSAGE,
      redirectToLogin: true,
    };
  } catch {
    return {
      success: false,
      message: RESET_PASSWORD_ERROR_MESSAGE,
      redirectToLogin: true,
    };
  }
};
